/* Tygrix global scripts: slider component, money helper, external links. */

window.txFormatMoney = function txFormatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const fmt = format || window.txMoneyFormat || '${{amount}}';
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision, thousands, decimal) {
    thousands = thousands ?? ',';
    decimal = decimal ?? '.';
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  const match = fmt.match(placeholder);
  if (!match) return fmt;
  let value;
  switch (match[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2);
  }
  return fmt.replace(placeholder, value);
};

if (!customElements.get('tx-slider')) {
  class TxSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.tx-slider__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prevButtons = this.querySelectorAll('[data-tx-prev]');
      this.nextButtons = this.querySelectorAll('[data-tx-next]');
      this.dotLists = this.querySelectorAll('[data-tx-dots]');
      this.loop = this.dataset.loop === 'true';
      this.mql = window.matchMedia('(min-width: 750px)');

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      this.prevButtons.forEach((btn) => btn.addEventListener('click', () => this.go(-1)));
      this.nextButtons.forEach((btn) => btn.addEventListener('click', () => this.go(1)));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.mql.addEventListener('change', this.update);
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      if (this.dataset.autoplay === 'true') {
        this.addEventListener('mouseenter', () => (this.paused = true));
        this.addEventListener('mouseleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
      }
      this.update();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
      this.mql?.removeEventListener('change', this.update);
    }

    get isSlider() {
      const key = this.mql.matches ? 'sliderDesktop' : 'sliderMobile';
      return this.dataset[key] !== 'false';
    }

    get perView() {
      const value = getComputedStyle(this).getPropertyValue('--tx-cols');
      return Math.max(1, parseFloat(value) || 1);
    }

    get perMove() {
      if (!this.mql.matches) return 1;
      return Math.max(1, parseInt(this.dataset.perMove || '1', 10));
    }

    get pageCount() {
      return Math.max(1, Math.ceil((this.slides.length - Math.floor(this.perView)) / this.perMove) + 1);
    }

    update() {
      const enabled = this.isSlider && this.slides.length > Math.floor(this.perView);
      this.classList.toggle('is-slider', enabled);
      this.buildDots();
      this.setAutoplay(enabled);
      this.onScroll();
    }

    buildDots() {
      const count = this.isSlider ? this.pageCount : 0;
      this.dotLists.forEach((list) => {
        if (list.children.length === count) return;
        list.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'tx-slider__dot';
          btn.setAttribute('aria-label', `Slide ${i + 1}`);
          btn.addEventListener('click', () => this.goToPage(i));
          li.appendChild(btn);
          list.appendChild(li);
        }
      });
    }

    slideStep() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    currentPage() {
      const step = this.slideStep() * this.perMove;
      if (!step) return 0;
      return Math.round(this.track.scrollLeft / step);
    }

    goToPage(page) {
      const step = this.slideStep() * this.perMove;
      this.track.scrollTo({ left: page * step, behavior: 'smooth' });
    }

    go(direction) {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      const atEnd = this.track.scrollLeft >= maxScroll;
      const atStart = this.track.scrollLeft <= 2;
      if (this.loop && direction > 0 && atEnd) return this.goToPage(0);
      if (this.loop && direction < 0 && atStart) return this.goToPage(this.pageCount - 1);
      this.goToPage(this.currentPage() + direction);
    }

    onScroll() {
      const page = this.currentPage();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      this.dotLists.forEach((list) => {
        Array.from(list.querySelectorAll('button')).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === page ? 'true' : 'false');
        });
      });
      if (this.loop) return;
      this.prevButtons.forEach((btn) => (btn.disabled = this.track.scrollLeft <= 2));
      this.nextButtons.forEach((btn) => (btn.disabled = this.track.scrollLeft >= maxScroll));
    }

    setAutoplay(enabled) {
      clearInterval(this.timer);
      if (!enabled || this.dataset.autoplay !== 'true') return;
      const speed = Math.max(2, parseFloat(this.dataset.speed || '5')) * 1000;
      this.timer = setInterval(() => {
        if (!this.paused) this.go(1);
      }, speed);
    }
  }
  customElements.define('tx-slider', TxSlider);
}

/* Open links to other websites in a new tab. */
document.addEventListener('DOMContentLoaded', () => {
  Array.from(document.links).forEach((link) => {
    if (link.hostname && link.hostname !== window.location.hostname) {
      link.target = '_blank';
      link.rel = 'noreferrer noopener';
    }
  });
});
